import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { getMyProfile, getLinks, listCvTraces, getCvTrace, getCvVideo } from '../lib/cortivoxApi';
import { chartRows, chartGroups, lanes, repStarts, label, traceProblem } from './fullMetrics/traceSeries';
import HelpButton from './fullMetrics/HelpButton';
import {
  PLOT_INSETS, fracOfT, tOfFrac, fracFromClientX, videoTimeOfT, tOfVideoTime, seekTarget, nearestFrame, nearestIndex,
} from './fullMetrics/timeSync';

// /demo/full-metrics: everything the CV module computed for one exercise session:
// the values on each frame next to the thresholds in force on that frame
// (personalisation changes them between repetitions), and which checks passed.
// The data is the trace from POST /api/cv-reports/server (lgp_cv_module). It is
// visible to whoever sees the reports: the patient or a doctor with an active link.

const VALUE_COLORS = ['#0f9d8a', '#2563eb', '#7c3aed', '#db2777'];
const THRESHOLD_COLORS = ['#dc2626', '#ea580c'];
const CATEGORY_COLORS = ['#94a3b8', '#f59e0b', '#ef4444', '#8b5cf6', '#3b82f6', '#ec4899', '#14b8a6', '#84cc16'];
const CORRECT_FEEDBACK = 'correct_hold';
const NO_VALUE = '#e2e8f0';
// As many as GET /api/cv-reports/traces returns (MAX_LISTED_TRACES).
const MAX_LISTED = 200;
// Plot margins: the Y axis on the left and a gap on the right. The check lanes are inset
// the same way, so the same time is on the same vertical in a lane and on a plot.
const Y_AXIS_WIDTH = PLOT_INSETS.left;
const RIGHT_MARGIN = PLOT_INSETS.right;
// A check lane is already inside the insets: its width is the whole plot area.
const NO_INSETS = { left: 0, right: 0 };

// The time cursor is one vertical line on every plot and lane. Its position is the
// CSS variable --cursor-frac on the TracePlots root, changed by setProperty, not by
// React: while the video plays the cursor moves every frame without a re-render.
const cursorStyle = (left) => ({ left, visibility: 'var(--cursor-visibility, hidden)' });
const CURSOR_CLASS = 'absolute top-0 bottom-0 w-0.5 -translate-x-1/2 bg-accent pointer-events-none';
const PLOT_CURSOR = cursorStyle(`calc(${Y_AXIS_WIDTH}px + (100% - ${Y_AXIS_WIDTH + RIGHT_MARGIN}px) * var(--cursor-frac, 0))`);
// A lane clips everything outside it (overflow-hidden): at the edges the line is kept inside.
const LANE_CURSOR = cursorStyle('clamp(1px, calc(100% * var(--cursor-frac, 0)), calc(100% - 1px))');
const SEEK_BOX = 'cursor-crosshair select-none touch-pan-y';

const CursorLine = ({ style }) => <div data-testid="cursor-line" aria-hidden="true" className={CURSOR_CLASS} style={style} />;

// A click, or a drag with the button held, puts the cursor under the pointer.
// onPick(frac, dragging): while dragging, the video seek can be deferred.
function seekHandlers(onPick, insets) {
  const fracOf = (e) => fracFromClientX(e.clientX, e.currentTarget.getBoundingClientRect(), insets);
  return {
    onClick: (e) => onPick(fracOf(e), false),
    // Pointer capture: the drag doesn't break off when the mouse leaves the plot.
    onPointerDown: (e) => {
      if (e.currentTarget.setPointerCapture && e.pointerId !== undefined) {
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* the pointer is already released */ }
      }
    },
    onPointerMove: (e) => { if (e.buttons & 1) onPick(fracOf(e), true); },
  };
}

const formatDate = (iso) => new Date(iso).toLocaleString('ru-RU', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

function sessionTitle(s) {
  const frames = s.frames == null ? '' : ` · ${s.frames} кадров`;
  return `${formatDate(s.createdAt)} · ${s.exerciseSlug}${frames}`;
}

function categoryColors(lane) {
  const colors = {};
  let next = 0;
  for (const { value } of lane.runs) {
    if (value in colors) continue;
    if (value === null) colors[value] = NO_VALUE;
    else colors[value] = value === CORRECT_FEEDBACK ? '#16a34a' : CATEGORY_COLORS[next++ % CATEGORY_COLORS.length];
  }
  return colors;
}

function Lane({ lane, t0, span, onPick }) {
  const colors = lane.kind === 'category' ? categoryColors(lane) : null;
  const colorOf = (value) => {
    if (colors) return colors[value];
    if (value === null) return NO_VALUE;
    if (lane.kind === 'state') return value ? '#3b82f6' : '#cbd5e1';
    return value ? '#16a34a' : '#ef4444';
  };
  return (
    <div style={{ paddingLeft: Y_AXIS_WIDTH, paddingRight: RIGHT_MARGIN }}>
      <div className="text-xs text-muted-foreground">{label(lane.name)}</div>
      <div>
        <div data-testid="lane-box" className={`relative h-4 rounded bg-muted overflow-hidden ${SEEK_BOX}`}
             {...seekHandlers(onPick, NO_INSETS)}>
          {lane.runs.map((r, i) => (
            <div
              key={i}
              title={`${r.from.toFixed(2)}–${r.to.toFixed(2)} с: ${r.value === null ? '—' : String(r.value)}`}
              data-testid={`run-${lane.name}`}
              className="absolute top-0 h-full"
              style={{
                left: `${((r.from - t0) / span) * 100}%`,
                // A one-frame run (the last one) must not vanish.
                width: `max(2px, ${((r.to - r.from) / span) * 100}%)`,
                background: colorOf(r.value),
              }}
            />
          ))}
          <CursorLine style={LANE_CURSOR} />
        </div>
        {colors && (
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
            {Object.entries(colors).map(([value, color]) => (
              <span key={value} className="flex items-center gap-1 text-xs text-muted-foreground">
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
                {value === 'null' ? '—' : value}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function videoProblem(err) {
  if (err.code === 'video_not_found') return 'оно ещё загружается или не сохранилось';
  if (err.code === 'video_disabled') return 'хранилище видео не настроено';
  return err.message;
}

// The session video above the plots, sticks to the top on scroll. It reports its
// time through onTime: while playing, on every shown frame
// (requestVideoFrameCallback, else requestAnimationFrame); when seeked with its
// own timeline, on seeking/seeked/timeupdate.
function SessionVideo({ reportId, videoRef, onTime, children }) {
  const [url, setUrl] = useState(null);
  const [problem, setProblem] = useState('');
  const retried = useRef(false);
  const resumeAt = useRef(null);
  const resumePlay = useRef(false);
  const loop = useRef(null);
  const urlRef = useRef(null);
  // The session this component is showing now; null when unmounted.
  // A refetch answer for another (or no) session is dropped.
  const liveReport = useRef(null);

  useEffect(() => {
    liveReport.current = reportId;
    const fresh = () => liveReport.current === reportId;
    getCvVideo(reportId)
      .then((v) => { if (fresh()) { urlRef.current = v.url; setUrl(v.url); } })
      .catch((err) => fresh() && setProblem(videoProblem(err)));
    return () => { liveReport.current = null; };
  }, [reportId]);

  const stopLoop = useCallback(() => {
    const l = loop.current;
    if (!l) return;
    if (l.video && l.video.cancelVideoFrameCallback) l.video.cancelVideoFrameCallback(l.id);
    else cancelAnimationFrame(l.id);
    loop.current = null;
  }, []);
  useEffect(() => stopLoop, [stopLoop]);

  const startLoop = () => {
    stopLoop();
    const video = videoRef.current;
    if (!video) return;
    const running = () => !video.paused && !video.ended;
    if (video.requestVideoFrameCallback) {
      const onFrame = (_now, meta) => {
        onTime(meta.mediaTime);
        if (running()) loop.current = { video, id: video.requestVideoFrameCallback(onFrame) };
        else loop.current = null;
      };
      loop.current = { video, id: video.requestVideoFrameCallback(onFrame) };
    } else {
      const onTick = () => {
        onTime(video.currentTime);
        if (running()) loop.current = { video: null, id: requestAnimationFrame(onTick) };
        else loop.current = null;
      };
      loop.current = { video: null, id: requestAnimationFrame(onTick) };
    }
  };

  const report = (e) => onTime(e.currentTarget.currentTime);
  const onPause = (e) => { stopLoop(); report(e); };
  // While playing, the frame loop drives the cursor; the infrequent timeupdate
  // would jerk it backwards, so it is only used while paused.
  const onTimeUpdate = (e) => { if (!loop.current) report(e); };
  const onLoadedMetadata = (e) => {
    // The file loaded: the next error (the link expired again) is again fixed by a refetch.
    retried.current = false;
    if (resumeAt.current === null) return;
    const video = e.currentTarget;
    video.currentTime = resumeAt.current;
    resumeAt.current = null;
    if (resumePlay.current) {
      resumePlay.current = false;
      const played = video.play();
      if (played && played.catch) played.catch(() => {});
    }
  };
  // The signed link expired (or the network blinked): a new link from the same
  // position. Two errors in a row without a successful load between them: a note.
  const onError = (e) => {
    stopLoop();
    if (retried.current) {
      setProblem('не удалось загрузить файл');
      return;
    }
    retried.current = true;
    const video = e.currentTarget;
    resumeAt.current = video.currentTime;
    resumePlay.current = !video.paused;
    const fresh = () => liveReport.current === reportId;
    getCvVideo(reportId)
      .then((v) => {
        if (!fresh()) return;
        // The same string won't change src: the player has to be reloaded by hand.
        if (v.url === urlRef.current) videoRef.current?.load();
        urlRef.current = v.url;
        setUrl(v.url);
      })
      .catch((err) => fresh() && setProblem(videoProblem(err)));
  };

  if (problem) {
    return (
      <div className="flex flex-col gap-1">
        <p className="text-sm text-muted-foreground">Видео сессии недоступно: {problem}.</p>
        {children}
      </div>
    );
  }
  return (
    <div className="-mx-1 px-1 py-2 bg-background flex flex-wrap items-end gap-4
                    lg:mx-0 lg:px-0 lg:py-0 lg:flex-col lg:items-start lg:gap-2">
      {url ? (
        <video
          ref={videoRef}
          src={url}
          controls
          muted
          playsInline
          preload="metadata"
          className="max-h-[30vh] lg:max-h-[60vh] max-w-full rounded-xl border border-border bg-muted"
          onPlay={startLoop}
          onPause={onPause}
          onEnded={onPause}
          onSeeking={report}
          onSeeked={report}
          onTimeUpdate={onTimeUpdate}
          onLoadedMetadata={onLoadedMetadata}
          onError={onError}
        />
      ) : (
        <p className="text-sm text-muted-foreground">Загрузка видео…</p>
      )}
      {children}
    </div>
  );
}

function TraceView({ session }) {
  const problem = traceProblem(session.trace);
  if (problem) return <p className="text-sm text-destructive">{problem}.</p>;
  return <TracePlots session={session} />;
}

function TracePlots({ session }) {
  const { columns } = session.trace;
  const { rows, step } = useMemo(() => chartRows(columns), [columns]);
  const groups = useMemo(() => chartGroups(columns, session.trace.exercise || session.exerciseSlug),
    [columns, session.trace.exercise, session.exerciseSlug]);
  const laneList = useMemo(() => lanes(columns), [columns]);
  const starts = useMemo(() => repStarts(columns), [columns]);
  const t0 = rows.length ? rows[0].t : 0;
  const span = rows.length ? Math.max(rows[rows.length - 1].t - t0, 0.001) : 0.001;
  const startMs = session.video?.startMs ?? 0;

  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const readoutRef = useRef(null);
  // The video handlers outlive a render, so they read the geometry from a ref.
  const geometry = useRef(null);
  useLayoutEffect(() => {
    geometry.current = { t0, span, startMs, columns };
  }, [t0, span, startMs, columns]);
  // The video seek while dragging: at most once per animation frame.
  const pendingSeek = useRef({ time: null, id: null });
  useEffect(() => () => {
    if (pendingSeek.current.id !== null) cancelAnimationFrame(pendingSeek.current.id);
  }, []);

  // Put the cursor at trace time t (s). No setState: only the CSS variable and the text.
  const showCursor = useCallback((t) => {
    const root = rootRef.current;
    if (!root) return;
    const g = geometry.current;
    const frac = fracOfT(t, g.t0, g.span);
    const inside = frac >= 0 && frac <= 1;
    root.style.setProperty('--cursor-frac', String(Math.min(1, Math.max(0, frac))));
    root.style.setProperty('--cursor-visibility', inside ? 'visible' : 'hidden');
    root.dataset.cursor = inside ? 'on' : 'off';
    if (readoutRef.current) {
      const frame = nearestFrame(g.columns, t);
      readoutRef.current.textContent = `${t.toFixed(2)} с · кадр ${frame ?? '—'}`;
    }
  }, []);

  // The video reported its time (playback, or a seek on its own timeline).
  const onVideoTime = useCallback(
    (v) => showCursor(tOfVideoTime(v, geometry.current.startMs)), [showCursor],
  );

  // A click on a plot or a lane snaps to the nearest trace frame, so the cursor, the
  // frame number and the video frame belong to one row. The cursor moves at once; the
  // video, while dragging, on the next animation frame with the latest value.
  const pick = useCallback((frac, dragging) => {
    const g = geometry.current;
    const i = nearestIndex(g.columns, tOfFrac(frac, g.t0, g.span));
    if (i < 0) return;
    const t = g.columns.t_ms[i] / 1000;
    showCursor(t);
    if (!videoRef.current) return;
    const target = seekTarget(videoTimeOfT(t, g.startMs));
    const pending = pendingSeek.current;
    if (!dragging) {
      if (pending.id !== null) cancelAnimationFrame(pending.id);
      pending.id = null;
      videoRef.current.currentTime = target;
      return;
    }
    pending.time = target;
    if (pending.id !== null) return;
    pending.id = requestAnimationFrame(() => {
      pending.id = null;
      if (videoRef.current) videoRef.current.currentTime = pending.time;
    });
  }, [showCursor]);

  useLayoutEffect(() => {
    if (rootRef.current) rootRef.current.dataset.cursor = 'off';
    if (readoutRef.current) readoutRef.current.textContent = 'щёлкните по графику';
  }, []);

  if (rows.length === 0) return <p className="text-sm text-muted-foreground">В trace нет кадров.</p>;
  const reps = session.report?.exercise?.info_reps || [];
  const plotSeek = seekHandlers(pick, PLOT_INSETS);
  const readout = (
    <span className="text-xs text-muted-foreground">
      Курсор: <span ref={readoutRef} data-testid="cursor-readout" className="text-primary font-semibold" />
    </span>
  );

  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,auto)_minmax(0,1fr)] lg:items-start lg:gap-6">
      {/* wide screens: the video stays at the left while the plots scroll; narrow: a strip on top */}
      {session.video && (
        <aside data-testid="video-column" className="sticky top-0 z-10 bg-background lg:top-4 lg:max-w-[40vw]">
          <SessionVideo reportId={session.id} videoRef={videoRef} onTime={onVideoTime}>{readout}</SessionVideo>
        </aside>
      )}
    <div ref={rootRef} data-testid="trace-root" className="flex flex-col gap-6 min-w-0 lg:col-start-2">
      <div className="text-sm text-primary flex flex-wrap gap-x-6 gap-y-1">
        <span>Упражнение: <b>{session.exerciseSlug}</b></span>
        <span>Кадров: <b>{session.trace.frames}</b> ({span.toFixed(1)} с)</span>
        <span>Повторов в отчёте: <b>{reps.length}</b></span>
        {step > 1 && (
          <span className="text-muted-foreground">на графиках минимум и максимум каждых {step} кадров</span>
        )}
      </div>
      {!session.video && readout}
      {session.trace.truncated && (
        <p className="text-sm text-destructive">Trace обрезан: сессия длиннее лимита CV-модуля.</p>
      )}

      {laneList.length > 0 && (
        <section className="rounded-2xl border border-border bg-background p-4 flex flex-col gap-2">
          <div className="flex items-center gap-1.5">
            <h2 className="text-sm font-semibold text-primary">Проверки по кадрам</h2>
            <HelpButton id="checks" title="Проверки по кадрам" />
          </div>
          {laneList.map((l) => <Lane key={l.name} lane={l} t0={t0} span={span} onPick={pick} />)}
        </section>
      )}

      {groups.map((g) => (
        <section key={g.id} className="rounded-2xl border border-border bg-background p-4">
          <div className="flex items-center gap-1.5 mb-2">
            <h2 className="text-sm font-semibold text-primary">{g.title}</h2>
            <HelpButton id={g.id} title={g.title} />
          </div>
          <div data-testid="plot-box" className={`relative h-56 ${SEEK_BOX}`} {...plotSeek}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} syncId="trace" margin={{ top: 4, right: RIGHT_MARGIN, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="t" type="number" domain={[t0, t0 + span]} allowDataOverflow unit=" с" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={Y_AXIS_WIDTH} />
                <Tooltip
                  labelFormatter={(t) => `${Number(t).toFixed(2)} с`}
                  formatter={(v, name) => [v === null ? '—' : v, name]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {starts.map((s) => (
                  <ReferenceLine key={`${s.t}-${s.repId}`} x={s.t} stroke="#94a3b8" strokeDasharray="2 4"
                                 label={{ value: `#${s.repId}`, position: 'insideTopLeft', fontSize: 10, fill: '#64748b' }} />
                ))}
                {g.zeroLine && (
                  <ReferenceLine y={0} stroke="#64748b" ifOverflow="extendDomain" data-testid="zero-line" />
                )}
                {g.referenceY !== undefined && (
                  <ReferenceLine y={g.referenceY} stroke="#64748b" strokeDasharray="4 4" ifOverflow="extendDomain"
                                 data-testid={`reference-y-${g.referenceY}`} />
                )}
                {g.values.map((name, i) => (
                  <Line key={name} dataKey={name} name={label(name)} stroke={VALUE_COLORS[i % VALUE_COLORS.length]}
                        dot={false} isAnimationActive={false} strokeWidth={1.5} />
                ))}
                {g.thresholds.map((name, i) => (
                  <Line key={name} dataKey={name} name={`${label(name)} (порог)`} type="stepAfter"
                        stroke={THRESHOLD_COLORS[i % THRESHOLD_COLORS.length]} strokeDasharray="6 3"
                        dot={false} isAnimationActive={false} strokeWidth={1.5} />
                ))}
              </LineChart>
            </ResponsiveContainer>
            <CursorLine style={PLOT_CURSOR} />
          </div>
        </section>
      ))}
    </div>
    </div>
  );
}

export default function FullMetricsPage() {
  const [me, setMe] = useState(null);
  const [signedOut, setSignedOut] = useState(false);
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState('');
  const [sessions, setSessions] = useState(null);
  const [sessionId, setSessionId] = useState('');
  const [session, setSession] = useState(null);
  const [loadingTrace, setLoadingTrace] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const profile = await getMyProfile();
        setMe(profile);
        if (profile.role === 'doctor') {
          const links = await getLinks();
          setPatients(links.filter((l) => l.status === 'active').map((l) => ({
            id: l.counterpart?.id || l.patient_id,
            name: l.counterpart?.full_name || l.counterpart?.email || l.patient_id,
          })));
        }
      } catch (err) {
        // Without a token cortivoxApi throws an ApiError without a status; a network error is not an ApiError.
        if (err.status === 401 || (err.name === 'ApiError' && !err.status)) setSignedOut(true);
        else setError(err.message);
      }
    })();
  }, []);

  // The session list: a patient's own, or the chosen patient's for a doctor.
  useEffect(() => {
    setSessions(null);
    setSessionId('');
    setSession(null);
    setError('');
    if (!me || !['patient', 'doctor'].includes(me.role) || (me.role === 'doctor' && !patientId)) return undefined;
    let cancelled = false;
    listCvTraces({ patientId: me.role === 'doctor' ? patientId : undefined })
      .then((list) => {
        if (cancelled) return;
        setSessions(list);
        if (list.length) setSessionId(list[0].id);
      })
      .catch((err) => !cancelled && setError(err.message));
    return () => { cancelled = true; };
  }, [me, patientId]);

  useEffect(() => {
    setSession(null);
    if (!sessionId) return undefined;
    let cancelled = false;
    setError('');
    setLoadingTrace(true);
    getCvTrace(sessionId)
      .then((data) => !cancelled && setSession(data))
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoadingTrace(false));
    // Moved on to another session or patient: this request won't show anything any more.
    return () => { cancelled = true; setLoadingTrace(false); };
  }, [sessionId]);

  const selectClass = 'rounded-xl border border-border bg-background px-3 py-2 text-sm text-primary max-w-full';

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-5xl lg:max-w-7xl px-4 py-8 flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-semibold text-primary">Полные метрики сессии</h1>
          <p className="text-sm text-muted-foreground">
            Демо: всё, что CV-модуль измерил на каждом кадре, и пороги, по которым он судил.
          </p>
        </div>

        {signedOut && (
          <p className="text-sm text-primary">
            Данные видны пациенту и его врачу.{' '}
            <Link to="/login" className="font-semibold text-accent underline">Войти</Link>
          </p>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}

        {me && (
          <div className="flex flex-wrap items-end gap-4">
            {me.role === 'doctor' && (
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                Пациент
                <select className={selectClass} value={patientId} onChange={(e) => setPatientId(e.target.value)}>
                  <option value="">— выберите —</option>
                  {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
            )}
            {sessions && sessions.length > 0 && (
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                Сессия
                <select className={selectClass} value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
                  {sessions.map((s) => <option key={s.id} value={s.id}>{sessionTitle(s)}</option>)}
                </select>
              </label>
            )}
          </div>
        )}

        {me && !['patient', 'doctor'].includes(me.role) && (
          <p className="text-sm text-muted-foreground">Страница доступна пациенту и врачу.</p>
        )}
        {sessions?.length >= MAX_LISTED && (
          <p className="text-xs text-muted-foreground">Показаны последние {MAX_LISTED} сессий.</p>
        )}
        {me?.role === 'doctor' && patients.length === 0 && (
          <p className="text-sm text-muted-foreground">Нет пациентов с активной связью.</p>
        )}
        {sessions && sessions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Нет сессий с покадровыми данными. Они появляются после упражнений «Заборчик», «Открой рот»,
            «Улыбка» и «Удивись» в тренажёре.
          </p>
        )}
        {loadingTrace && <p className="text-sm text-muted-foreground">Загрузка…</p>}
        {session?.id === sessionId && !loadingTrace && <TraceView session={session} />}
      </div>
    </div>
  );
}
